import React, { useState, useEffect, Fragment } from 'react';
import { 
  Box, 
  Button, 
  Popover, 
  Typography, 
  IconButton, 
  Badge, 
  Tooltip,
  CircularProgress,
  Divider,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { useFilters } from '../../../contexts/FiltersContext';
import analyticsService from '../../../services/analyticsApi';
import PanelSection from '../SidePanel/PanelSection';
import { useAuthStore } from '../../../store/authStore';
import { AccountType } from '../../../types/auth';
import type { PanelData } from '../SidePanel/_shared';

// Map a raw value to its display value, collapsing "*main*" entries to "NA".
const naValue = (v: any): string =>
  typeof v === 'string' && v.toLowerCase().includes('main') ? 'NA' : v;

// Build the API request params from the current context selections (pure).
const buildFilterParams = (ctx: any): Record<string, any> => {
  const params: any = {};
  if (ctx.licenseeData?.length > 0) params.licensees = ctx.licenseeData.map((i: any) => i.id);
  if (ctx.subCustomerData?.length > 0) params.customers = ctx.subCustomerData.map((i: any) => i.id);
  if (ctx.subCompanyData?.length > 0) params.subCompanies = ctx.subCompanyData.map((i: any) => i.id);
  if (ctx.branchData?.length > 0) params.branches = ctx.branchData.map((i: any) => i.id);
  if (ctx.countryOriginData?.length > 0) params.originCountries = ctx.countryOriginData.map((i: any) => i.id);
  if (ctx.countryDestinationData?.length > 0) params.destinationCountries = ctx.countryDestinationData.map((i: any) => i.id);
  if (ctx.cityOriginData?.length > 0) params.originCities = ctx.cityOriginData.map((i: any) => i.id);
  if (ctx.cityDestinationData?.length > 0) params.destinationCities = ctx.cityDestinationData.map((i: any) => i.id);
  if (ctx.transportModeData?.length > 0) params.transportModes = ctx.transportModeData.map((i: any) => i.id);
  return params;
};

const checklist = (key: string, label: string, values: any[]): PanelData =>
  ({ type: 'checklist', key, label, values });

// Build the per-filter PanelData objects from the API response (pure).
const buildPanelData = (filters: any): Record<string, PanelData | null> => ({
  licensee: filters.licensees?.length > 0
    ? checklist('licensee', 'Licensee', filters.licensees.map((l: any) => ({ ...l, isChecked: false })))
    : null,
  subCustomer: filters.customers?.length > 0
    ? checklist('subCustomer', 'Sub Customer', filters.customers.map((c: any) => ({ id: c, value: naValue(c), isChecked: false })))
    : null,
  subCompany: filters.subCompanies?.length > 0
    ? checklist('subCompany', 'Sub Company', filters.subCompanies.map((sc: any) => ({ id: sc, value: naValue(sc), isChecked: false })))
    : null,
  branch: filters.branches?.length > 0
    ? checklist('branch', 'Branch', filters.branches.map((b: any) => ({ id: b, value: naValue(b), isChecked: false })))
    : null,
  countryOrigin: filters.originCountries?.length > 0
    ? checklist('countryOrigin', 'Origin Country', filters.originCountries.map((c: any) => ({ id: c, value: c, isChecked: false })))
    : null,
  countryDestination: filters.destinationCountries?.length > 0
    ? checklist('countryDestination', 'Destination Country', filters.destinationCountries.map((c: any) => ({ id: c, value: c, isChecked: false })))
    : null,
  cityOrigin: filters.originCities?.length > 0
    ? checklist('cityOrigin', 'Origin City', filters.originCities.map((c: any) => ({ id: c, value: c, isChecked: false })))
    : null,
  cityDestination: filters.destinationCities?.length > 0
    ? checklist('cityDestination', 'Destination City', filters.destinationCities.map((c: any) => ({ id: c, value: c, isChecked: false })))
    : null,
  transportMode: filters.transportModes?.length > 0
    ? checklist('transportMode', 'Transport Mode', filters.transportModes.map((tm: any) => ({ id: tm, value: tm, isChecked: false })))
    : null,
});

const HeaderFilters: React.FC = () => {
  const useFilterContext = useFilters();
  const { user } = useAuthStore();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Panel data states
  const [licenseeData, setLicenseeData] = useState<PanelData | null>(null);
  const [subCompanyData, setSubCompanyData] = useState<PanelData | null>(null);
  const [branchData, setBranchData] = useState<PanelData | null>(null);
  const [subCustomerData, setSubCustomerData] = useState<PanelData | null>(null);
  const [countryOriginData, setCountryOriginData] = useState<PanelData | null>(null);
  const [countryDestinationData, setCountryDestinationData] = useState<PanelData | null>(null);
  const [cityOriginData, setCityOriginData] = useState<PanelData | null>(null);
  const [cityDestinationData, setCityDestinationData] = useState<PanelData | null>(null);
  const [transportModeData, setTransportModeData] = useState<PanelData | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const shouldShowFilter = (filterType: string): boolean => {
    if (!user) return false;
    switch (user.accountType) {
      case AccountType.MasterAdmin:
      case AccountType.CarbonCareAdmin: return true;
      case AccountType.LicenseeAdmin:
      case AccountType.User: return filterType !== 'licensee';
      case AccountType.SubCompanyAdmin: return !['licensee', 'subCompany'].includes(filterType);
      case AccountType.BranchAdmin: return !['licensee', 'subCompany', 'branch'].includes(filterType);
      case AccountType.SubCustomerAdmin: return ['subCompany', 'countryOrigin', 'countryDestination', 'cityOrigin', 'cityDestination', 'transportMode'].includes(filterType);
      default: return false;
    }
  };

  const loadAllFilters = async () => {
    try {
      setIsLoading(true);
      const params = buildFilterParams(useFilterContext);
      const { data } = await analyticsService.getData(analyticsService.endpoints.filters.filterHierarchy, params);

      if (data.success) {
        const panels = buildPanelData(data.filters);
        const setters: Record<string, (d: PanelData) => void> = {
          licensee: setLicenseeData,
          subCustomer: setSubCustomerData,
          subCompany: setSubCompanyData,
          branch: setBranchData,
          countryOrigin: setCountryOriginData,
          countryDestination: setCountryDestinationData,
          cityOrigin: setCityOriginData,
          cityDestination: setCityDestinationData,
          transportMode: setTransportModeData,
        };
        for (const [key, set] of Object.entries(setters)) {
          const panel = panels[key];
          if (panel) set(panel);
        }
      }
    } catch (error) {
      console.error('Failed to load filters:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadAllFilters();
    }
  }, [open, useFilterContext.licenseeData, useFilterContext.subCustomerData, useFilterContext.subCompanyData, useFilterContext.branchData, useFilterContext.countryOriginData, useFilterContext.countryDestinationData, useFilterContext.cityOriginData, useFilterContext.cityDestinationData, useFilterContext.transportModeData]);

  const totalSelected = [
    useFilterContext.licenseeData,
    useFilterContext.subCompanyData,
    useFilterContext.branchData,
    useFilterContext.subCustomerData,
    useFilterContext.countryOriginData,
    useFilterContext.countryDestinationData,
    useFilterContext.cityOriginData,
    useFilterContext.cityDestinationData,
    useFilterContext.transportModeData,
  ].reduce((acc, curr) => acc + (curr?.length || 0), 0);

  // Filters hidden from the UI for now (still wired; re-enable by removing the key).
  const hiddenFilterKeys = new Set(['subCompany', 'branch']);

  return (
    <Box>
      <Tooltip title="Advanced Filters">
        <Button
          variant="outlined"
          size="small"
          onClick={handleClick}
          startIcon={
            <Badge badgeContent={totalSelected} color="primary">
              <FilterListIcon />
            </Badge>
          }
          sx={{ height: 40, borderRadius: 2 }}
        >
          Filters
        </Button>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              maxHeight: 600,
              mt: 1,
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              borderRadius: 3,
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(144, 191, 69, 0.08)' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Filters</Typography>
          <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <Divider />
        <Box sx={{ p: 2, overflowY: 'auto', maxHeight: 500 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <Fragment>
              {licenseeData && shouldShowFilter('licensee') && (
                <PanelSection panelItem={licenseeData} type={licenseeData.type} handleSidePanelSelection={() => {}} />
              )}
              {subCompanyData && !hiddenFilterKeys.has('subCompany') && shouldShowFilter('subCompany') && (
                <PanelSection panelItem={subCompanyData} type={subCompanyData.type} handleSidePanelSelection={() => {}} />
              )}
              {branchData && !hiddenFilterKeys.has('branch') && shouldShowFilter('branch') && (
                <PanelSection panelItem={branchData} type={branchData.type} handleSidePanelSelection={() => {}} />
              )}
              {subCustomerData && shouldShowFilter('subCustomer') && (
                <PanelSection panelItem={subCustomerData} type={subCustomerData.type} handleSidePanelSelection={() => {}} />
              )}
              {countryOriginData && shouldShowFilter('countryOrigin') && (
                <PanelSection panelItem={countryOriginData} type={countryOriginData.type} handleSidePanelSelection={() => {}} />
              )}
              {countryDestinationData && shouldShowFilter('countryDestination') && (
                <PanelSection panelItem={countryDestinationData} type={countryDestinationData.type} handleSidePanelSelection={() => {}} />
              )}
              {cityOriginData && shouldShowFilter('cityOrigin') && (
                <PanelSection panelItem={cityOriginData} type={cityOriginData.type} handleSidePanelSelection={() => {}} />
              )}
              {cityDestinationData && shouldShowFilter('cityDestination') && (
                <PanelSection panelItem={cityDestinationData} type={cityDestinationData.type} handleSidePanelSelection={() => {}} />
              )}
              {transportModeData && shouldShowFilter('transportMode') && (
                <PanelSection panelItem={transportModeData} type={transportModeData.type} handleSidePanelSelection={() => {}} />
              )}
            </Fragment>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default HeaderFilters;
